import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { FormGroup, Input } from 'reactstrap'
import EditableRow from '~/components/EditableRow'
import useMultiPropState from '~/hooks/useMultiPropState'
import _ from 'lodash'
import Select from '~/components/Select'
import {
  brazilianStates,
  spanishProvinces,
  usStates,
} from '~/constants/location'
import styled from 'styled-components'

import { getLocalizedAddressVersion } from '~/utility/getLocalizedAddressVersion'

const ADMINAREA_COVERED_COUNTRIES = ['BR', 'ES', 'US']

const AddressField = ({
  controller,
  label,
  onSwitchChange,
  editableFields,
}) => {
  const switchId = `storefrontAdressSwitch`

  const [inputs, setInputValue, setInputs] = useMultiPropState({
    addressLines_0: '',
    addressLines_1: '',
    administrativeArea: '',
    locality: '',
    postalCode: '',
    regionCode: 'BR',
    languageCode: 'pt',
  })

  const intl = useIntl()

  const brazilianStatesOptions = React.useMemo(() => {
    if (inputs.regionCode === 'BR')
      return brazilianStates.map(({ state, uf }) => ({
        label: state,
        value: uf,
      }))
    if (inputs.regionCode === 'ES')
      return spanishProvinces.map(province => ({
        label: province,
        value: province,
      }))
    if (inputs.regionCode === 'US')
      return usStates.map(state => ({
        label: state,
        value: state,
      }))

    return []
  }, [inputs.regionCode])

  const selectedState = React.useMemo(() => {
    if (inputs.administrativeArea) {
      return brazilianStatesOptions.find(
        option =>
          option.value.toLowerCase() === inputs.administrativeArea.toLowerCase()
      )
    }
  }, [inputs.administrativeArea, brazilianStatesOptions])

  const setFormikValue = React.useCallback(
    _.debounce((name, value) => controller.setFieldValue(name, value), 250),
    []
  )

  const handleInputChange = React.useCallback(
    (name, value) => {
      setInputValue(name, value)
      if (name.includes('_')) {
        const [field, position] = name.split('_')
        setFormikValue(`storefrontAddress.${field}.[${position}]`, value)
      } else {
        setFormikValue(`storefrontAddress.${name}`, value)
      }
    },
    [setInputValue, setFormikValue]
  )

  const searchByPostalCode = React.useCallback(
    async (inputPostalCode = '') => {
      const postalCode = inputPostalCode.replaceAll('-', '').replaceAll('.', '')
      if (postalCode.length < 8) return

      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${postalCode}/json`
        )
        const data = await response.json()
        if (data) {
          handleInputChange('administrativeArea', data?.uf)
          handleInputChange('addressLines_0', data.logradouro)
          handleInputChange('sublocality', data.bairro)
          handleInputChange('locality', data.localidade)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [handleInputChange]
  )

  const fillDefaultAdressValues = React.useCallback(
    initialValues => {
      const address = initialValues?.storefrontAddress
      if (address) {
        const { addressLines, ...rest } = address
        const [addressLines_0, addressLines_1] = addressLines || []
        setInputs({ addressLines_0, addressLines_1, ...rest })
      }
    },
    [setInputs]
  )

  React.useEffect(() => {
    fillDefaultAdressValues(controller.initialValues)
  }, [controller.initialValues, fillDefaultAdressValues])

  return (
    <>
      <EditableRow
        switchId={switchId}
        onChange={id => {
          if (editableFields?.[switchId]) {
            fillDefaultAdressValues(controller.initialValues)
          }
          onSwitchChange(id)
        }}
        checkboxStyle={{
          alignSelf: 'start',
          marginTop: '27px',
        }}
        isChecked={!!editableFields?.[switchId]}
      >
        <FormGroup>
          <label htmlFor="storefrontAdress.street">
            <FormattedMessage id={label} />:
          </label>
          <div className="row mt-50">
            <div className="col-md-6 pr-50">
              <LabelField filled={inputs.postalCode}>
                <label>
                  <FormattedMessage
                    id={getLocalizedAddressVersion(
                      'postalCode',
                      inputs.regionCode
                    )}
                  />
                </label>
                <Input
                  id="storefrontAdress.postalCode"
                  name="storefrontAdress.postalCode"
                  disabled={!editableFields?.[switchId]}
                  value={inputs.postalCode || ''}
                  placeholder={intl.formatMessage({
                    id: getLocalizedAddressVersion(
                      'postalCode',
                      inputs.regionCode
                    ),
                  })}
                  onChange={e => {
                    handleInputChange('postalCode', e.target.value)
                    searchByPostalCode(e.target.value)
                  }}
                  maxLength={9}
                />
              </LabelField>
            </div>
            {ADMINAREA_COVERED_COUNTRIES.includes(inputs.regionCode) && (
              <div className="col-md-6 pl-50">
                <LabelField filled={selectedState}>
                  <label>
                    <FormattedMessage
                      id={getLocalizedAddressVersion(
                        'administrativeArea',
                        inputs.regionCode
                      )}
                    />
                  </label>
                  <Select
                    className="mb-1"
                    isDisabled={!editableFields?.[switchId]}
                    value={selectedState}
                    options={brazilianStatesOptions}
                    placeholder={intl.formatMessage({
                      id: getLocalizedAddressVersion(
                        'administrativeArea',
                        inputs.regionCode
                      ),
                    })}
                    onChange={e =>
                      handleInputChange('administrativeArea', e.value)
                    }
                    menuPortalTarget={document.querySelector('body')}
                  />
                </LabelField>
              </div>
            )}
            {!ADMINAREA_COVERED_COUNTRIES.includes(inputs.regionCode) && (
              <div className="col-md-6 pl-50">
                <LabelField filled={inputs.administrativeArea}>
                  <label>
                    <FormattedMessage
                      id={getLocalizedAddressVersion(
                        'administrativeArea',
                        inputs.regionCode
                      )}
                    />
                  </label>
                  <Input
                    className="mb-1"
                    id="storefrontAdress.administrativeArea"
                    name="storefrontAdress.administrativeArea"
                    disabled={!editableFields?.[switchId]}
                    value={inputs.administrativeArea || ''}
                    placeholder={intl.formatMessage({
                      id: getLocalizedAddressVersion(
                        'administrativeArea',
                        inputs.regionCode
                      ),
                    })}
                    onChange={e => {
                      handleInputChange('administrativeArea', e.target.value)
                      searchByPostalCode(e.target.value)
                    }}
                    maxLength={9}
                  />
                </LabelField>
              </div>
            )}
          </div>
          <LabelField filled={inputs.addressLines_0}>
            <label>
              <FormattedMessage id="Street" />
            </label>
            <Input
              id="storefrontAdress.street"
              name="storefrontAdress.street"
              disabled={!editableFields?.[switchId]}
              placeholder={intl.formatMessage({ id: 'Street' })}
              value={inputs.addressLines_0 || ''}
              onChange={e =>
                handleInputChange('addressLines_0', e.target.value)
              }
            />
          </LabelField>
          <LabelField filled={inputs.sublocality}>
            <label>
              <FormattedMessage
                id={getLocalizedAddressVersion(
                  'sublocality',
                  inputs.regionCode
                )}
              />
            </label>
            <Input
              id="storefrontAdress.sublocality"
              name="storefrontAdress.sublocality"
              className="mt-1"
              placeholder={intl.formatMessage({
                id: getLocalizedAddressVersion(
                  'sublocality',
                  inputs.regionCode
                ),
              })}
              disabled={!editableFields?.[switchId]}
              value={inputs.sublocality || ''}
              onChange={e => handleInputChange('sublocality', e.target.value)}
            />
          </LabelField>
          <LabelField filled={inputs.locality}>
            <label>
              <FormattedMessage
                id={getLocalizedAddressVersion('locality', inputs.regionCode)}
              />
            </label>
            <Input
              id="storefrontAdress.locality"
              name="storefrontAdress.locality"
              className="mt-1"
              placeholder={intl.formatMessage({
                id: getLocalizedAddressVersion('locality', inputs.regionCode),
              })}
              disabled={!editableFields?.[switchId]}
              value={inputs.locality || ''}
              onChange={e => handleInputChange('locality', e.target.value)}
            />
          </LabelField>
        </FormGroup>
      </EditableRow>
    </>
  )
}

const LabelField = styled.div`
  position: relative;

  label {
    display: ${props => (props.filled ? 'block' : 'none')};
    position: absolute;
    left: 8px;
    top: -8px;
    background: #fff;
    border-radius: 2px;
    z-index: 1;
  }
`

export default AddressField
